/**
 * GeneralStatusDistribution — Faza B v2
 *
 * Card Dashboard care afișează distribuția celor 8 Statusuri Generale în format
 * GRID (1 sub-card per status, cu count + procent + click-through).
 *
 * Layout: 4 coloane pe desktop, 2 pe tablet, 1 pe mobil.
 * Click pe un card → navighează la /sales?general_status=<status>&preset=... .
 * Bara de sumar afișează: total, warnings, conflicts, rework, stale.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AlertOctagon, AlertTriangle, CheckCircle, Clock, HelpCircle,
  Send, Ban, RotateCcw, RefreshCw, Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  "Nou":                 { Icon: HelpCircle,   color: "slate",   qs: "Nou" },
  "În verificare BO":    { Icon: RefreshCw,    color: "purple",  qs: "În verificare BO" },
  "În procesare PPC":    { Icon: Send,         color: "blue",    qs: "În procesare PPC" },
  "În curs de activare": { Icon: Clock,        color: "cyan",    qs: "În curs de activare" },
  "Activ":               { Icon: CheckCircle,  color: "emerald", qs: "Activ" },
  "Problemă":            { Icon: AlertOctagon, color: "rose",    qs: "Problemă" },
  "Anulat":              { Icon: Ban,          color: "orange",  qs: "Anulat" },
  "Stornat":             { Icon: RotateCcw,    color: "red",     qs: "Stornat" },
};

const COLOR_CLASSES = {
  slate:   "bg-slate-50 border-slate-300 text-slate-800",
  purple:  "bg-purple-50 border-purple-300 text-purple-800",
  blue:    "bg-blue-50 border-blue-300 text-blue-800",
  cyan:    "bg-cyan-50 border-cyan-300 text-cyan-800",
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
  rose:    "bg-rose-50 border-rose-300 text-rose-800",
  orange:  "bg-orange-50 border-orange-300 text-orange-800",
  red:     "bg-red-50 border-red-300 text-red-800",
};

export const GeneralStatusDistribution = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/dashboard/general-status-distribution`);
        setData(r.data);
      } catch (e) {
        console.error("Failed to load general status distribution", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const total = data?.total || 0;
  const counts = data?.counts || {};

  return (
    <Card data-testid="general-status-distribution-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity /> Distribuție Status General
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </CardTitle>
          <div className="text-xs text-slate-500">
            Total: <span className="font-semibold text-slate-800">{total}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Grid cu 8 carduri */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="gs-dist-grid">
          {(data?.distribution || []).map(({ status, count }) => {
            const meta = STATUS_META[status] || STATUS_META["Nou"];
            const { Icon, color, qs } = meta;
            const cls = COLOR_CLASSES[color] || COLOR_CLASSES.slate;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const href = `/sales?general_status=${encodeURIComponent(qs)}`;
            return (
              <Link
                to={href}
                key={status}
                className={`block rounded-lg border p-3 transition-all hover:shadow-md hover:-translate-y-0.5 ${cls}`}
                data-testid={`gs-dist-card-${qs}`}
                title={`${count} vânzări · ${pct}% din total. Click pentru a filtra.`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-semibold truncate">{status}</span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{count}</span>
                  <span className="text-[10px] text-slate-500">{pct}%</span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Sub-KPI bar — separare clară: conflicte blocante (necesită atenție) vs. avertismente calitate date */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t" data-testid="gs-dist-kpis">
          <Link
            to="/sales?preset=blocking_conflicts"
            className="p-2 rounded border border-red-200 bg-red-50 hover:bg-red-100 transition-colors"
            data-testid="gs-kpi-blocking"
            title="Conflicte reale care necesită verificare: QC final negativ fără activare autoritară sau cu Rework activ."
          >
            <div className="flex items-center gap-1 text-[10px] text-red-700 uppercase font-semibold">
              <AlertOctagon className="h-3 w-3" /> Conflicte blocante
            </div>
            <div className="text-lg font-bold text-red-800">{counts.with_blocking_conflict || 0}</div>
            <div className="text-[9px] text-red-600 leading-tight mt-0.5">necesită atenție</div>
          </Link>
          <Link
            to="/sales?preset=warnings_only"
            className="p-2 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
            data-testid="gs-kpi-warnings"
            title="Semnale de calitate a datelor (ex: QC pozitiv fără dată apel, fără agent asociat, activare neconfirmată de Salesforce). NU blochează statusul."
          >
            <div className="flex items-center gap-1 text-[10px] text-amber-700 uppercase font-semibold">
              <AlertTriangle className="h-3 w-3" /> Avertismente calitate date
            </div>
            <div className="text-lg font-bold text-amber-800">{counts.warnings_only ?? counts.with_warnings ?? 0}</div>
            <div className="text-[9px] text-amber-600 leading-tight mt-0.5">nu blochează</div>
          </Link>
          <Link
            to="/sales?preset=rework_active"
            className="p-2 rounded border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors"
            data-testid="gs-kpi-rework"
          >
            <div className="flex items-center gap-1 text-[10px] text-orange-700 uppercase font-semibold">
              <RotateCcw className="h-3 w-3" /> Rework activ
            </div>
            <div className="text-lg font-bold text-orange-800">{counts.requires_rework || 0}</div>
          </Link>
          <Link
            to="/sales?preset=stale_status"
            className="p-2 rounded border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 transition-colors"
            data-testid="gs-kpi-stale"
          >
            <div className="flex items-center gap-1 text-[10px] text-yellow-800 uppercase font-semibold">
              <RefreshCw className="h-3 w-3" /> Neactualizate
            </div>
            <div className="text-lg font-bold text-yellow-900">{counts.stale || 0}</div>
          </Link>
        </div>

        {/* Notă explicativă — de ce „conflictele" nu mai sunt afișate ca un singur număr */}
        <p className="text-[10px] text-slate-500 leading-snug" data-testid="gs-kpi-explainer">
          <span className="font-semibold text-slate-700">Conflictele blocante</span> sunt cazurile care necesită
          verificare. <span className="font-semibold text-slate-700">Avertismentele</span> semnalează doar calitatea
          datelor (ex: lipsă dată apel QC, fără agent asociat) și <span className="font-semibold">nu blochează</span> statusul.
        </p>
      </CardContent>
    </Card>
  );
};

// small inline icon to avoid extra import
const Activity = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 text-teal-600" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export default GeneralStatusDistribution;
